import { ModelBase } from '../abstract/model-base.model';
import { AgentAction } from './agent-action.model';
import { AgentToolCall } from './agent-tool-call.model';
import { AgentUsage } from 'src/app/@core/services/ai/agent-relay.client';

/** Avis de l'utilisateur sur la réponse d'un tour (#1284) : pouce levé ou baissé. */
export type AgentTurnFeedback = 'positive' | 'negative';

/** Un tour de conversation de l'agent IA, pour le rejeu de l'historique (#877). */
export class AgentTurn extends ModelBase {

    static override CLASS_NAME: string = 'AgentTurn';

    private _turnIndex!: number;
    private _userMessage!: string;
    private _agentMessage!: string;
    private _agentThoughts!: string;
    private _modelVersion!: string;
    private _usage!: AgentUsage | null;
    private _toolCalls!: AgentToolCall[];
    private _actions!: AgentAction[];
    private _canUndo!: boolean;
    private _feedback!: AgentTurnFeedback | null;
    private _feedbackComment!: string;
    // creationDate est hérité de ModelBase

    public get turnIndex(): number {
        return this._turnIndex;
    }

    public set turnIndex(value: any) {
        this._setValue('_turnIndex', value, 'integer');
    }

    public get userMessage(): string {
        return this._userMessage;
    }

    public set userMessage(value: any) {
        this._setValue('_userMessage', value, 'string');
    }

    public get agentMessage(): string {
        return this._agentMessage;
    }

    public set agentMessage(value: any) {
        this._setValue('_agentMessage', value, 'string');
    }

    /** Résumé de raisonnement du tour (supervision uniquement, §9.1). Vide si aucun. */
    public get agentThoughts(): string {
        return this._agentThoughts ?? '';
    }

    public set agentThoughts(value: any) {
        this._setValue('_agentThoughts', value, 'string');
    }

    /** Version exacte du modèle ayant servi ce tour. */
    public get modelVersion(): string {
        return this._modelVersion;
    }

    public set modelVersion(value: any) {
        this._setValue('_modelVersion', value, 'string');
    }

    /** Tokens + coût brut de ce tour (`cost` peut être `null`). */
    public get usage(): AgentUsage | null {
        return this._usage;
    }

    public set usage(value: any) {
        this._setValue('_usage', value, 'any');
    }

    /** Journal ordonné des appels d'outils du tour (supervision uniquement, §9.1). */
    public get toolCalls(): AgentToolCall[] {
        return this._toolCalls ?? [];
    }

    public set toolCalls(value: any) {
        this._setValue('_toolCalls', value, 'AgentToolCall');
    }

    /**
     * Écritures de l'agent pendant le tour, dans l'ordre (#1217) : mêmes cartes que
     * l'event SSE `action`, servies à tous — les boutons sont calculés pour qui rejoue.
     */
    public get actions(): AgentAction[] {
        return this._actions ?? [];
    }

    public set actions(value: any) {
        this._setValue('_actions', value, 'AgentAction');
    }

    /**
     * Reste-t-il à l'utilisateur quelque chose à annuler dans ce tour (« Annuler cet échange ») :
     * un marqueur que ce tour a posé, ou une proposition à retirer.
     */
    public get canUndo(): boolean {
        return this._canUndo;
    }

    public set canUndo(value: any) {
        this._setValue('_canUndo', value, 'boolean');
    }

    /** Avis donné sur la réponse du tour (#1284) ; `null` sans avis. */
    public get feedback(): AgentTurnFeedback | null {
        return this._feedback ?? null;
    }

    public set feedback(value: any) {
        this._setValue('_feedback', value, 'string');
    }

    /** Commentaire de l'avis, vide sans commentaire. */
    public get feedbackComment(): string {
        return this._feedbackComment ?? '';
    }

    public set feedbackComment(value: any) {
        this._setValue('_feedbackComment', value, 'string');
    }
}
